/**
 * BON-210 — Auth provider selector.
 *
 * Mounted by main.tsx in place of `<Auth0Provider>`. Renders the real
 * `<Auth0Provider>` normally; only on a LOCAL dev server with a preview token
 * injected does it mount `<PreviewAuthProvider>` (authenticates with an injected
 * REAL Auth0 token — see ./PreviewAuthProvider).
 *
 * Production safety is structural, not a special guard:
 *  - `import.meta.env.DEV` is a Vite build-mode constant — `true` only under the
 *    `vite` dev server, `false` in any `vite build` (every deploy, staging or
 *    prod). So `PREVIEW_AUTH_ENABLED` is statically `false` in a built bundle and
 *    the whole preview branch (plus the imported preview modules) is dead-code-
 *    eliminated. The affordance literally cannot run in a deployed build.
 *  - And even if it somehow did, the backend only ever trusts real Auth0 tokens —
 *    there is no bypass to exploit.
 */
import type { ReactNode } from 'react';

import PreviewAuthProvider from './PreviewAuthProvider';
import { previewAuthEnabled } from './previewToken';
import { RealAuth0Provider } from './RealAuth0Provider';

export function AuthProvider({ children }: { children: ReactNode }) {
  // Decided per render, not at module load: whether the token is still good and
  // whether this browser has opted out are both runtime facts.
  if (previewAuthEnabled()) {
    return <PreviewAuthProvider>{children}</PreviewAuthProvider>;
  }
  return <RealAuth0Provider>{children}</RealAuth0Provider>;
}

export default AuthProvider;
